import React, { useEffect, useState , useRef } from 'react';
import { fmtDateTime } from '../../utils/region';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import Pagination, { getPageSize } from '../Pagination';
import { brandOr } from '../../utils/brand';
import { useMarketCities } from '../../utils/firmContact';
import MarketSubtitle from './MarketSubtitle';

// Rows-per-page now follows the shared pager preference (25/50/100/200).

const fmtDT = (u: number | null) => u ? fmtDateTime(u) : '';
const STATUSES = ['new', 'contacted', 'qualified', 'converted', 'rejected'] as const;
const ST_COLOR: Record<string, { bg: string; fg: string }> = {
  new: { bg: '#EEF2FF', fg: '#4338CA' }, contacted: { bg: '#E4F0FB', fg: '#2B6CB0' }, qualified: { bg: '#FEF3C7', fg: '#B45309' },
  converted: { bg: '#DCFCE7', fg: '#15803D' }, rejected: { bg: '#FCE7E7', fg: '#B91C1C' },
};

const input: React.CSSProperties = { width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' });

const COLDEFS: { key: string; w: number }[] = [
  { key: 'am_col_name', w: 190 }, { key: 'am_col_category', w: 120 }, { key: 'am_col_city', w: 100 }, { key: 'am_col_area', w: 130 },
  { key: 'am_col_address', w: 220 }, { key: 'am_col_phone', w: 130 }, { key: 'am_col_email', w: 190 },
  { key: 'am_col_whatsapp', w: 130 }, { key: 'am_col_source', w: 150 }, { key: 'am_col_status', w: 110 },
  { key: 'am_col_recorded', w: 150 }, { key: '_actions', w: 80 },
];
const TABLE_W = COLDEFS.reduce((s, c) => s + c.w, 0);

// The cities to prospect are a workspace setting (Admin → Companies → Workspace → AI Marketing cities) — useMarketCities().
const EMPTY = { name: '', category: '', city: '', area: '', address: '', phone: '', email: '', whatsapp: '', website: '', source: '', source_url: '', status: 'new', notes: '', tat: '' };

// ── Add / edit modal ────────────────────────────────────────────────────────
function BizForm({ row, onClose, onSaved }: { row: any | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const marketCities = useMarketCities();
  const isNew = !row;
  const [f, setF] = useState<any>(row ? { ...EMPTY, ...row } : { ...EMPTY });
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));
  const [err, setErr] = useState(''); const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!String(f.name || '').trim()) { setErr(t('am_name_required')); return; }
    setSaving(true); setErr('');
    const data: any = {}; Object.keys(EMPTY).forEach(k => { data[k] = f[k] ?? ''; });
    try {
      if (isNew) await api.amCreate(data); else await api.amUpdate(row.id, data);
      onSaved(); onClose();
    } catch (e: any) { setErr(e.message); setSaving(false); }
  };
  const field = (label: string, k: string, ph = '') => (
    <div><label style={lbl}>{label}</label><input value={f[k] || ''} onChange={e => set(k, e.target.value)} placeholder={ph} style={input} /></div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 560, maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>{isNew ? t('am_add_title') : t('am_edit_title')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {field(t('am_col_name'), 'name')}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>{field(t('am_col_category'), 'category', t('am_category_ph'))}</div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>{t('am_col_city')}</label>
              <input list="am-cities" value={f.city || ''} onChange={e => set('city', e.target.value)} placeholder={t('am_col_city')} style={input} />
              <datalist id="am-cities">{marketCities.map(c => <option key={c} value={c} />)}</datalist>
            </div>
            <div style={{ flex: 1 }}>{field(t('am_col_area'), 'area', t('am_area_ph'))}</div>
          </div>
          {field(t('am_col_address'), 'address')}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>{field(t('am_col_phone'), 'phone')}</div>
            <div style={{ flex: 1 }}>{field(t('am_col_whatsapp'), 'whatsapp')}</div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>{field(t('am_col_email'), 'email')}</div>
            <div style={{ flex: 1 }}>{field(t('am_col_website'), 'website')}</div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>{t('am_col_source')}</label>
              <input list="am-sources" value={f.source || ''} onChange={e => set('source', e.target.value)} placeholder="Google Maps / LinkedIn / Google …" style={input} />
              <datalist id="am-sources"><option value="Google Maps" /><option value="Google" /><option value="LinkedIn" /><option value="Website" /><option value="Facebook" /><option value="Instagram" /></datalist>
            </div>
            <div style={{ flex: 1 }}>{field(t('am_col_source_url'), 'source_url')}</div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>{t('am_col_status')}</label>
              <select value={f.status || 'new'} onChange={e => set('status', e.target.value)} style={input}>
                {STATUSES.map(s => <option key={s} value={s}>{t('am_st_' + s)}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>{field(t('am_col_tat'), 'tat', t('am_tat_ph'))}</div>
          </div>
          <div><label style={lbl}>{t('am_col_notes')}</label><textarea value={f.notes || ''} onChange={e => set('notes', e.target.value)} rows={3} style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }} /></div>
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
            <button onClick={save} disabled={saving} style={btn('var(--accent)')}>{saving ? t('login_wait') : t('save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Read-only quick preview (opens on a row click) ──────────────────────────
function BizPreview({ row, onClose, onEdit }: { row: any; onClose: () => void; onEdit: () => void }) {
  const { t } = useTranslation();
  const sc = ST_COLOR[row.status] || ST_COLOR.new;
  const Row = (label: string, node: React.ReactNode) => (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ width: 120, flexShrink: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{node || '—'}</div>
    </div>
  );
  const link = (url: string, text?: string) => url ? <a href={/^https?:\/\//.test(url) ? url : 'https://' + url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{text || url}</a> : null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 560, maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <i className="ti ti-building-store" style={{ color: 'var(--accent)' }} />
          <div style={{ fontSize: 15, fontWeight: 800, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</div>
          <button onClick={onEdit} title={t('edit')} style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-app)', color: 'var(--text-secondary)', cursor: 'pointer', padding: '6px 10px', fontSize: 13 }}><i className="ti ti-pencil" style={{ marginRight: 5 }} />{t('edit')}</button>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }}><i className="ti ti-x" /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px' }}>
          {/* badges */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 10, background: sc.bg, color: sc.fg }}>{t('am_st_' + (row.status || 'new'))}</span>
            {row.discovered_by === 'ai' && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 10, background: 'var(--bg-active)', color: 'var(--accent)' }}><i className="ti ti-sparkles" style={{ fontSize: 11 }} /> AI</span>}
            {row.tat && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 10, background: '#EDE9FE', color: '#6D28D9' }}>TAT{typeof row.tat === 'string' && row.tat !== 'yes' ? ' ' + row.tat : ''}</span>}
            {row.client_match === 'active' && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 10, background: '#DCFCE7', color: '#15803D' }}>{t('am_badge_client')}{row.client_firms ? ' · ' + row.client_firms : ''}</span>}
            {row.client_match === 'inactive' && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 10, background: '#FFEDD5', color: '#C2410C' }}>{t('am_badge_former')}</span>}
          </div>
          {Row(t('am_col_category'), row.category)}
          {Row(t('am_col_city'), row.city)}
          {Row(t('am_col_area'), row.area)}
          {Row(t('am_col_address'), row.address)}
          {Row(t('am_col_phone'), row.phone)}
          {Row(t('am_col_email'), row.email)}
          {Row(t('am_col_whatsapp'), row.whatsapp)}
          {Row(t('am_col_website'), link(row.website))}
          {Row(t('am_col_source'), <span>{row.source}{row.source_url ? <> · {link(row.source_url, t('am_link'))}</> : null}</span>)}
          {Row(t('am_col_tat'), row.tat)}
          {Row(t('am_col_notes'), <span style={{ whiteSpace: 'pre-wrap' }}>{row.notes}</span>)}
          {Row(t('am_col_recorded'), <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{row.created_by_name || '—'} · {fmtDT(row.created_at)}{row.updated_at && row.updated_at !== row.created_at ? ' · ✎ ' + fmtDT(row.updated_at) : ''}</span>)}
        </div>
      </div>
    </div>
  );
}

export default function AiMarketing({ embedded }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const marketCities = useMarketCities();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(getPageSize());
  const [areas, setAreas] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [city, setCity] = useState('all');
  const [area, setArea] = useState('all');
  const [rel, setRel] = useState('all');
  const [discoverCity, setDiscoverCity] = useState<string | null>(null);   // city-picker modal for a new crawl
  const [showAll, setShowAll] = useState(false);   // show businesses with no WhatsApp & no email
  const [rematching, setRematching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState<any | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [preview, setPreview] = useState<any | null>(null);
  const [job, setJob] = useState<any | null>(null);

  const load = async (p = 1) => {
    setLoading(true); setErr('');
    try {
      const r: any = await api.amList({ q, status, city, area, rel, all: showAll ? '1' : '', page: p, limit: pageSize });
      setRows(r.rows || []); setTotal(r.total || 0); setAreas(r.areas || []); setCities(r.cities || []); setPage(r.page || p);
    } catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(1); }, [status, city, area, rel, showAll, pageSize]); // eslint-disable-line
  // Skip the mount run — it double-loaded the list on open (owner 2026-09-06).
  const qInit = useRef(true);
  useEffect(() => { if (qInit.current) { qInit.current = false; return; } const id = setTimeout(() => load(1), 350); return () => clearTimeout(id); }, [q]); // eslint-disable-line

  // Poll discovery status; refresh the table as rows stream in, and once when it finishes.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const s: any = await api.amDiscoverStatus();
        if (!alive) return;
        const wasRunning = job?.running;
        setJob(s);
        if (s.running) { if (s.inserted !== (job?.inserted ?? -1)) load(); }
        else if (wasRunning) load();   // just finished
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [job?.running, job?.inserted]); // eslint-disable-line

  const runDiscover = async (pickedCity: string) => {
    setDiscoverCity(null);
    try { await api.amDiscoverStart({ city: pickedCity }); const s: any = await api.amDiscoverStatus(); setJob(s); if (city === 'all' || city === pickedCity) load(); }
    catch (e: any) { setErr(e.message); }
  };
  const stopDiscover = async () => { try { await api.amDiscoverCancel(); } catch { /* ignore */ } };
  const rematch = async () => {
    setRematching(true);
    try { const r: any = await api.amRematch(); alert(t('am_rematch_done', { clients: r.clients || 0, former: r.inactive || 0 })); load(); }
    catch (e: any) { setErr(e.message); } finally { setRematching(false); }
  };
  const importClients = async () => {
    if (!window.confirm(t('am_import_confirm'))) return;
    setRematching(true);
    try { const r: any = await api.amImportClients(); alert(t('am_import_done', { added: r.added || 0, active: r.addedActive || 0, former: r.addedInactive || 0, already: r.alreadyInMarketing || 0 })); load(); }
    catch (e: any) { setErr(e.message); } finally { setRematching(false); }
  };

  const del = async (row: any) => {
    if (!window.confirm(t('am_confirm_delete', { name: row.name || '' }))) return;
    try { await api.amDelete(row.id); load(); } catch (e: any) { setErr(e.message); }
  };

  // 11 → 12px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"), and the line
  // under the headings is an inset shadow where it was a bottom border ("Make sure to include the horizontal gray line
  // for any pages that scroll"). The table collapses its borders, and a collapsed border stays with the table when the
  // sticky heading moves, so the headings lost their line as soon as the list scrolled; a shadow is the cell's own.
  // Then Active Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system
  // look like this: the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the
  // regular weight, where they were dark grey in bold, on one 16px line over Active Jobs' 1px --border-strong line, where
  // it was 2px. The shadow lies over the cell's last pixel where a border would sit under it, so 10px above and 11
  // below, where both were 9, make the band 37px as every list's is. The 12px and the lines between the columns stay.
  // The gap under the text is 5px tighter now, 11 → 6px (report c151d269: "Bring the heading text 5 pixels closer to
  // the grey line below. Do this for every page."), which leaves the band 32px — level with the bordered lists, whose
  // 10px bottom went to 5 over a 1px line.
  const th: React.CSSProperties = { fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px 6px', lineHeight: '16px', boxShadow: 'inset 0 -1px 0 var(--border-strong)', borderRight: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-surface)', zIndex: 1, textAlign: 'left', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-primary)', padding: '8px 10px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', verticalAlign: 'top', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const sel: React.CSSProperties = { padding: '7px 10px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', cursor: 'pointer' };

  return (
    <div style={{ ...(embedded ? { flex: 1, minHeight: 0 } : { height: '100vh' }), display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      {!embedded && <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_ai_marketing')}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}><MarketSubtitle cities={marketCities} /></div>
        </div>
      </header>}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
          <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: 'var(--text-tertiary)' }} aria-hidden />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('am_search_ph')} style={{ ...input, paddingLeft: 30 }} />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} style={sel}>
          <option value="all">{t('am_all_status')}</option>
          {STATUSES.map(s => <option key={s} value={s}>{t('am_st_' + s)}</option>)}
        </select>
        <select value={city} onChange={e => setCity(e.target.value)} style={sel}>
          <option value="all">{t('am_all_cities')}</option>
          {/* Always list the crawlable cities (even with no data yet) + any others already in the data. */}
          {Array.from(new Set([...marketCities, ...cities])).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={area} onChange={e => setArea(e.target.value)} style={sel}>
          <option value="all">{t('am_all_areas')}</option>
          {areas.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={rel} onChange={e => setRel(e.target.value)} style={sel}>
          <option value="all">{t('am_rel_all')}</option>
          <option value="client">{t('am_rel_client')}</option>
          <option value="former">{t('am_rel_former')}</option>
          <option value="tat">{t('am_rel_tat')}</option>
        </select>
        <button onClick={rematch} disabled={rematching} title={t('am_rematch_hint')} style={{ ...sel, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}><i className={rematching ? 'ti ti-loader' : 'ti ti-users'} style={{ fontSize: 14 }} />{t('am_rematch')}</button>
        <button onClick={importClients} disabled={rematching} title={t('am_import_hint')} style={{ ...sel, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}><i className="ti ti-user-plus" style={{ fontSize: 14 }} />{t('am_import')}</button>
        <label title={t('am_show_nocontact_hint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />{t('am_show_nocontact')}
        </label>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('am_count', { n: total })}</span>
        <div style={{ flex: 1 }} />
        {job?.running
          ? <button onClick={stopDiscover} style={btn('#FCE7E7', '#B91C1C')}><i className="ti ti-player-stop" style={{ fontSize: 14, marginRight: 6 }} aria-hidden />{t('am_discover_stop')}</button>
          : <button onClick={() => setDiscoverCity(city !== 'all' && marketCities.includes(city) ? city : (marketCities[0] || null))} disabled={!marketCities.length} title={marketCities.length ? undefined : t('fc_no_market_cities')} style={btn('#1D7C4D')}><i className="ti ti-sparkles" style={{ fontSize: 14, marginRight: 6 }} aria-hidden />{t('am_discover')}</button>}
        <button onClick={() => { setEditing(null); setShowForm(true); }} style={btn('var(--accent)')}><i className="ti ti-plus" style={{ fontSize: 14, marginRight: 6 }} aria-hidden />{t('am_add')}</button>
      </div>

      {job && (job.running || job.finishedAt) && (
        <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', background: job.running ? '#F0FDF4' : 'var(--bg-surface)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
            {job.running
              ? <span style={{ fontWeight: 700, color: '#15803D' }}><i className="ti ti-loader" style={{ marginRight: 5 }} />{t('am_discover_running')}{job.city ? ` · ${job.city}` : ''}</span>
              : <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}><i className="ti ti-circle-check" style={{ marginRight: 5, color: '#15803D' }} />{t('am_discover_done')}{job.city ? ` · ${job.city}` : ''}</span>}
            <span style={{ color: 'var(--text-secondary)' }}>{t('am_discover_progress', { done: job.doneCells, total: job.totalCells })}</span>
            <span style={{ color: '#15803D', fontWeight: 600 }}>+{job.inserted} {t('am_discover_added')}</span>
            <span style={{ color: 'var(--text-tertiary)' }}>{job.found} {t('am_discover_found')} · {job.skipped} {t('am_discover_dup')}</span>
            {job.running && job.current && <span style={{ color: 'var(--text-tertiary)' }}>· {job.current}</span>}
          </div>
          <div style={{ height: 5, borderRadius: 3, background: 'var(--border)', marginTop: 7, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${job.totalCells ? Math.round((job.doneCells / job.totalCells) * 100) : 0}%`, background: '#1D7C4D', transition: 'width .4s' }} />
          </div>
          {job.error && <div style={{ marginTop: 6, fontSize: 11.5, color: '#B91C1C' }}><i className="ti ti-alert-triangle" style={{ marginRight: 4 }} />{job.error}</div>}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto' }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div>
        ) : (
          <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%', minWidth: TABLE_W }}>
            <colgroup>{COLDEFS.map(c => <col key={c.key} style={{ width: c.w }} />)}</colgroup>
            <thead><tr>{COLDEFS.map(c => <th key={c.key} style={{ ...th, textAlign: c.key === '_actions' ? 'right' : 'left' }}>{c.key === '_actions' ? '' : t(c.key)}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => {
                const sc = ST_COLOR[r.status] || ST_COLOR.new;
                return (
                  <tr key={r.id} onClick={() => setPreview(r)} title={t('am_preview_open')} style={{ background: i % 2 ? 'var(--c-gray-50)' : 'transparent', cursor: 'pointer' }}>
                    <td style={{ ...td, whiteSpace: 'normal' }} title={r.name}>
                      <span style={{ fontWeight: 600 }}>{r.name}</span>
                      {r.discovered_by === 'ai' && <i className="ti ti-sparkles" title="AI" style={{ fontSize: 12, color: 'var(--accent)', marginLeft: 5 }} />}
                      <span style={{ display: 'inline-flex', gap: 4, marginLeft: 6, flexWrap: 'wrap', verticalAlign: 'middle' }}>
                        {r.tat && <span title={typeof r.tat === 'string' && r.tat !== 'yes' ? 'TAT ' + r.tat : 'TAT licensed'} style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 9, background: '#EDE9FE', color: '#6D28D9' }}>TAT</span>}
                        {r.client_match === 'active' && <span title={r.client_firms} style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 9, background: '#DCFCE7', color: '#15803D' }}>{t('am_badge_client')}{r.client_firms ? ' · ' + r.client_firms : ''}</span>}
                        {r.client_match === 'inactive' && <span title={r.client_firms} style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 9, background: '#FFEDD5', color: '#C2410C' }}>{t('am_badge_former')}</span>}
                      </span>
                    </td>
                    <td style={td} title={r.category}>{r.category || '—'}</td>
                    <td style={td} title={r.city}>{r.city || '—'}</td>
                    <td style={td} title={r.area}>{r.area || '—'}</td>
                    <td style={td} title={r.address}>{r.address || '—'}</td>
                    <td style={td} title={r.phone}>{r.phone || '—'}</td>
                    <td style={td} title={r.email}>{r.email || '—'}</td>
                    <td style={td} title={r.whatsapp}>{r.whatsapp || '—'}</td>
                    <td style={td} title={(r.source || '') + (r.source_url ? ' · ' + r.source_url : '')}>
                      {r.source_url ? <a href={r.source_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--accent)' }}>{r.source || t('am_link')}</a> : (r.source || '—')}
                    </td>
                    <td style={td}><span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: sc.bg, color: sc.fg, whiteSpace: 'nowrap' }}>{t('am_st_' + (r.status || 'new'))}</span></td>
                    <td style={{ ...td, whiteSpace: 'normal', fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                      <div>{r.created_by_name || '—'}</div><div>{fmtDT(r.created_at)}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => { setEditing(r); setShowForm(true); }} title={t('edit')} style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', marginRight: 4 }}><i className="ti ti-pencil" /></button>
                      <button onClick={() => del(r)} title={t('delete')} style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)', padding: 40 }} colSpan={COLDEFS.length}>{t('am_empty')}</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} total={total} limit={pageSize} onPage={p => load(p)} onLimit={n => { setPageSize(n); }} />

      {preview && <BizPreview row={preview} onClose={() => setPreview(null)} onEdit={() => { setEditing(preview); setShowForm(true); setPreview(null); }} />}
      {showForm && <BizForm row={editing} onClose={() => setShowForm(false)} onSaved={() => load(page)} />}
      {discoverCity && <DiscoverDialog city={discoverCity} onClose={() => setDiscoverCity(null)} onStart={runDiscover} />}
    </div>
  );
}

// ── Discover city-picker: choose which city to crawl, then kick off the AI run ──
function DiscoverDialog({ city, onClose, onStart }: { city: string; onClose: () => void; onStart: (city: string) => void }) {
  const { t } = useTranslation();
  const marketCities = useMarketCities();
  const [pick, setPick] = useState(city);
  const [cells, setCells] = useState<number | null>(null);
  useEffect(() => {
    let alive = true; setCells(null);
    api.amDiscoverOptions(pick).then((o: any) => { if (alive) setCells((o.areas?.length || 0) * (o.categories?.length || 0)); }).catch(() => {});
    return () => { alive = false; };
  }, [pick]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 440, maxWidth: '92vw', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}><i className="ti ti-sparkles" style={{ marginRight: 7, color: '#1D7C4D' }} />{t('am_discover_title')}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginTop: 4, marginBottom: 16 }}>{t('am_discover_pick_city')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {marketCities.map(c => (
            <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid ' + (pick === c ? 'var(--accent)' : 'var(--border-strong)'), background: pick === c ? 'var(--bg-active)' : 'var(--bg-app)', cursor: 'pointer' }}>
              <input type="radio" name="am-city" checked={pick === c} onChange={() => setPick(c)} />
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{c}</span>
            </label>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 12 }}>{t('am_discover_confirm', { cells: cells ?? '…' })}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
          <button onClick={() => onStart(pick)} style={btn('#1D7C4D')}><i className="ti ti-player-play" style={{ marginRight: 6 }} />{t('am_discover_start')}</button>
        </div>
      </div>
    </div>
  );
}
